<script setup lang="ts">
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { SUREbusinessCommonDomainRelatiesSoortRelatieEnum } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesSoortRelatieEnum';
import {
  useRelatiesGetKredietstatus,
  useRelatiesGetRelationByRelatienummer,
} from '@services/crm-public/orval-api/relaties-api/relaties-api';
import VMenu from '@shared/component/VMenu/VMenu.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, toRefs } from 'vue';
import { isOnderdeelBeschikbaar } from '@auth/marktsegmentBeschikbaarheid';
import { useClaims } from '@auth/useClaims';

import { type RouteRecord } from '../types';
import RelatiegegevensHeader from './RelatiegegevensHeader.vue';
import RelatiegegevensFooter from './RelatiegegevensFooter.vue';

const props = defineProps<{
  relatienummer: number;
  routes: RouteRecord[];
}>();

const { relatienummer } = toRefs(props);

const claims = useClaims();

const { error, data: kredietstatusResponse } = useRelatiesGetKredietstatus(
  computed(() => relatienummer.value),
  {
    query: { select: (r) => r.result },
  },
);
const kredietstatus = computed(() => {
  if (!kredietstatusResponse.value) {
    return undefined;
  }

  const isWarning = kredietstatusResponse.value.status === 'Oranje';
  const isError = kredietstatusResponse.value.status === 'Rood';

  const heeftBetalingsachterstand = isWarning || isError;
  if (!heeftBetalingsachterstand) {
    return undefined;
  }

  return {
    icon: isWarning ? 'fa-solid fa-exclamation-triangle' : 'fa-solid fa-exclamation-circle',
    color: isWarning ? 'text-orange' : 'text-red',
  };
});

const relatie = useRelatiesGetRelationByRelatienummer(relatienummer);

const menuItems = computed(() => {
  const items = props.routes
    .filter((route) => isOnderdeelBeschikbaar(route.meta.onderdeel, claims.value.marktsegment))
    .filter((r) => (r.meta.label === 'Naselectie' ? claims.value.isInternal : true))
    .filter(
      (r) =>
        r.meta.label !== 'UBO' ||
        relatie.data.value?.result?.typeName === SUREbusinessCommonDomainRelatiesSoortRelatieEnum.Z,
    )
    .map((route) => ({
      label: route.meta.label,
      icon: route.meta.icon as string,
      route: { name: route.name },
    }));

  return items;
});

useWatchErrorHandler(error, {
  onError:
    'Het ophalen van de kredietstatus is mislukt, hierdoor kan niet getoond worden of de relatie een betalingsachterstand heeft.',
});
</script>

<template>
  <div
    class="flex flex-col divide-x-0 divide-y divide-solid divide-grey-200 xl:h-[calc(100dvh-var(--header-height))]"
  >
    <RelatiegegevensHeader :relatienummer="props.relatienummer" />
    <div class="p-2 xl:grow">
      <VMenu class="w-full" theme="secondary" :model="menuItems">
        <template #item="{ item, styles: { textColor, activeBgColor, hoverColor } }">
          <RouterLink
            v-if="item.route"
            v-slot="{ href, navigate, isActive }"
            :to="item.route"
            custom
          >
            <a
              :href="href"
              @click="navigate"
              class="flex items-center gap-2 rounded px-2 py-2 text-sm overflow-wrap-anywhere hover:no-underline"
              :class="[textColor, hoverColor, { [activeBgColor]: isActive, 'font-bold': isActive }]"
            >
              <FontAwesomeIcon
                v-if="item.icon"
                class="shrink-0 [--fa-fw-width:16px]"
                fixed-width
                :icon="(isActive ? 'fas ' : 'far ') + item.icon"
              />
              {{ item.label }}
              <FontAwesomeIcon
                v-if="item.label === 'Incasso' && kredietstatus"
                :icon="kredietstatus.icon"
                class="ml-auto"
                :class="kredietstatus.color"
              />
            </a>
          </RouterLink>
        </template>
      </VMenu>
    </div>
    <RelatiegegevensFooter :relatienummer="props.relatienummer" />
  </div>
</template>
